import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wallet, Download, BarChart3, FileText, Users } from 'lucide-react';
import { topupAPI, securitfeeAPI, formatCurrency, formatNumber } from '../services/api';
import PDFExporter from '../utils/pdfExporter';
import { buildCsvRaw, downloadCsv } from '../utils/csvExport';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

const normalizeOperator = (value) => String(value || '').trim().toUpperCase();

const findTopupStats = (operator, topupRows = []) => {
  const key = normalizeOperator(operator);
  const row = topupRows.find((r) => normalizeOperator(r.operator) === key);
  return {
    transaction_count: Number(row?.transaction_count || 0),
    total_amount: Number(row?.total_amount || 0),
    unique_subscribers: Number(row?.unique_subscribers || 0),
  };
};

const ReportTopup = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [byOperator, setByOperator] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [month, setMonth] = useState(DEFAULT_REPORT_MONTH);
  const [year, setYear] = useState(DEFAULT_REPORT_YEAR);
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when filters change
  }, [month, year, operatorFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { month, year };

      const [topupRes, byOpRes] = await Promise.all([
        topupAPI.getReport(params),
        securitfeeAPI.getByOperator(params),
      ]);

      const rawByOp = Array.isArray(byOpRes) ? byOpRes : (byOpRes?.data || []);
      const topupRows = (topupRes?.data?.by_operator || []).map((row) => ({
        operator: row.operator || 'Unknown',
        transaction_count: row.transaction_count || 0,
        total_amount: row.total_amount || 0,
        unique_subscribers: row.unique_subscribers || 0,
      }));

      const fullList = rawByOp.map((r) => {
        const operator = r.operatorName || r.operator_name || r.operator;
        const stats = findTopupStats(operator, topupRows);
        return {
          operator,
          transaction_count: stats.transaction_count,
          total_amount: stats.total_amount,
          unique_subscribers: stats.unique_subscribers,
        };
      });

      setOperatorOptions(fullList.map((r) => r.operator).filter(Boolean));
      const list = operatorFilter === 'all'
        ? fullList
        : fullList.filter((x) => String(x.operator) === String(operatorFilter));
      setByOperator(list);
    } catch (err) {
      console.error(err);
      setError(err.message || t('reportTopup.loadError'));
      setByOperator([]);
    } finally {
      setLoading(false);
    }
  };

  const reportPeriodLabel = () => {
    const m = MONTHS.find((mo) => mo.value === month);
    return `${m?.label ?? month} ${year}`;
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    const monthName = MONTHS.find((m) => m.value === month)?.label || 'Current';
    const filename = `topup-report-${monthName.toLowerCase()}-${year}`;
    const result = await PDFExporter.exportToPDF('report-topup', filename, { scale: 1.5 });
    if (!result.success) alert(t('common.exportPdfFailed') + ': ' + result.error);
    setIsExporting(false);
  };

  const totals = {
    transaction_count: byOperator.reduce((s, r) => s + (r.transaction_count || 0), 0),
    total_amount: byOperator.reduce((s, r) => s + (r.total_amount || 0), 0),
    unique_subscribers: byOperator.reduce((s, r) => s + (r.unique_subscribers || 0), 0),
  };

  const handleExportCSV = () => {
    const periodLabel = reportPeriodLabel();
    const generated = new Date().toISOString().slice(0, 10);
    const rows = [
      ['Report', 'Top-up Report'],
      ['Period', periodLabel],
      ['Generated', generated],
      [],
      ['Total Transactions', 'Total Amount', 'Unique Subscribers'],
      [totals.transaction_count, totals.total_amount, totals.unique_subscribers],
      [],
      ['Operator', 'Transactions', 'Total Amount', 'Unique Subscribers'],
      ...byOperator.map((r) => [r.operator, r.transaction_count, r.total_amount, r.unique_subscribers]),
    ];
    const csv = buildCsvRaw(rows);
    const filename = `topup-report-${periodLabel.replace(/\s/g, '-')}`;
    downloadCsv(filename, csv);
  };

  return (
    <div id="report-topup" className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 print:shadow-none">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Wallet className="h-8 w-8 text-primary-600" />
                {t('reportTopup.title')}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">{t('common.appBrand')}</p>
              <p className="text-sm text-gray-600 mt-2">
                {t('common.reportPeriod')}: <strong>{reportPeriodLabel()}</strong>
                {operatorFilter !== 'all' && <> · {t('common.operator')}: <strong>{operatorFilter}</strong></>}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('common.generated')}: {new Date().toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleExportCSV}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <FileText className="h-4 w-4" />
                {t('common.exportCSV')}
              </button>
              <button
                type="button"
                onClick={handleExportPDF}
                disabled={isExporting}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <Download className="h-4 w-4" />
                {isExporting ? t('common.exporting') : t('common.exportPDF')}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.month')}</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>{t(`common.monthNames.${m.value}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.year')}</label>
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.operator')}</label>
                <select
                  value={operatorFilter}
                  onChange={(e) => setOperatorFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">{t('common.allOperators')}</option>
                  {operatorOptions.map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {t('common.reportPeriod')}: <strong className="text-gray-700">{reportPeriodLabel()}</strong>
            {operatorFilter !== 'all' && (
              <> · {t('common.operator')}: <strong className="text-gray-700">{operatorFilter}</strong></>
            )}
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-red-800">
            <span className="text-red-500 text-lg">{t('common.failedToLoad')}</span>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4" />
            <p className="text-gray-600">{t('reportTopup.loading')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-primary-100 rounded-lg">
                    <Wallet className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('reportTopup.totalTransactions')}</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{formatNumber(totals.transaction_count)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-green-100 rounded-lg">
                    <Wallet className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.totalAmount')}</p>
                    <p className="text-2xl font-bold text-green-700 tabular-nums">{formatCurrency(totals.total_amount)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-100 rounded-lg">
                    <Users className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.uniqueSubscribers')}</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{formatNumber(totals.unique_subscribers)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">{t('reportTopup.summaryByOperator')}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.operator')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.transactions')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.totalAmount')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.uniqueSubscribers')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {byOperator.map((row) => (
                      <tr key={row.operator} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-sm font-medium text-gray-900">{row.operator}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{formatNumber(row.transaction_count)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatCurrency(row.total_amount)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{formatNumber(row.unique_subscribers)}</td>
                      </tr>
                    ))}
                    {byOperator.length > 0 && (
                      <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                        <td className="px-5 py-3 text-sm text-gray-900">{t('common.total')}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{formatNumber(totals.transaction_count)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatCurrency(totals.total_amount)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{formatNumber(totals.unique_subscribers)}</td>
                      </tr>
                    )}
                    {byOperator.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-5 py-10 text-center text-sm text-gray-500">
                          {t('common.noDataForPeriod')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportTopup;
